import type { ResourceScope } from '@kop/contracts';
import { riskAnnualItemMetrics } from '@kop/risk-assessment';
import type { VictimRegistryPage } from '@kop/incidents-emergency';
import type { RoutineTbmLedgerPage, HistoricalDailyTbmLedgerPage } from '@kop/tbm-firebase-adapter';
import { createSessionKeyedReadCache } from '../../runtime/session-read-cache';
import { TBM_DATA_CHANGED_EVENT } from '../../runtime/tbm-data-events';
import { loadOperationsSummary, type OperationsSummary } from './operations-summary-port';

export function seoulWorkDate(value: string) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? ''
    : parsed.toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });
}

export function summarizeDashboardTbm(
  current: RoutineTbmLedgerPage,
  historical: HistoricalDailyTbmLedgerPage,
  day: string,
) {
  if (
    !current.exhausted ||
    current.nextCursor ||
    current.heldCount ||
    current.rows.length !== current.reconciliation.expectedCount ||
    !historical.exhausted ||
    historical.nextCursor ||
    historical.heldCount ||
    historical.rows.length !== historical.totalCount
  )
    return null;
  const rows = current.rows.filter(
    (row) =>
      row.date === day &&
      ['submitted', 'under-review', 'resubmitted', 'recorded', 'approved', 'closed'].includes(row.status),
  );
  const old = historical.rows.filter((row) => row.businessDate === day);
  const count = rows.length + old.length;
  const missing = old.some((row) => row.evidenceIssues.includes('MISSING_ATTENDANCE'));
  return {
    count,
    companies: new Set([...rows.map((row) => row.company), ...old.map((row) => row.contractorName)])
      .size,
    attendance: missing
      ? null
      : rows.reduce((sum, row) => sum + row.attendanceCount, 0) +
        old.reduce((sum, row) => sum + row.attendanceCount, 0),
    turnout: missing
      ? null
      : rows.reduce((sum, row) => sum + row.turnoutCount, 0) +
        old.reduce((sum, row) => sum + row.attendanceCount, 0),
  };
}

export function authoritativeVictimTotal(page: VictimRegistryPage): number {
  if (
    !page.summaryIncluded ||
    page.heldCount ||
    !Number.isSafeInteger(page.totalCount) ||
    page.totalCount < 0
  )
    throw Error('VICTIM_REGISTRY_SUMMARY_INCOMPLETE');
  return page.totalCount;
}

export async function readDashboard(input: {
  uid: string;
  scope: ResourceScope;
  day: string;
  month: string;
  features: readonly string[];
  fresh?: boolean;
}, onSummary?: (summary: OperationsSummary) => void) {
  if (
    input.scope.tenantId !== 'kop' ||
    input.scope.kind === 'tenant' ||
    !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(input.scope.siteId)
  )
    throw Error('SCOPE_UNAVAILABLE');
  const scope = input.scope;
  const contractorId = scope.kind === 'contractor' ? scope.contractorId : null;
  const can = (id: string) => input.features.includes(id);
  const errors: string[] = [];
  const safe = async <T>(enabled: boolean, read: () => Promise<T>): Promise<T | null> =>
    enabled ? read().catch((error: unknown) => {
      const code = error && typeof error === 'object' && 'code' in error ? String(error.code) : 'unavailable';
      if (['unauthenticated', 'permission-denied', 'functions/unauthenticated', 'functions/permission-denied'].includes(code)) throw error;
      errors.push(code);
      return null;
    }) : null;
  const [tbm, work, victimTotal, risk, operations] = await Promise.all([
    safe(can('tbm') && !!contractorId, async () => {
      const { getTbmAuthoritativeAdapter } = await import('../tbm/tbm-data-adapter');
      const adapter = getTbmAuthoritativeAdapter();
      const query = {
        tenantId: 'kop' as const,
        siteId: scope.siteId,
        actorId: input.uid,
        contractorId: contractorId ?? 'all-contractors',
        businessDate: input.day,
        pageSize: 30,
      };
      const [current, historical] = await Promise.all([
        adapter.loadRoutineTbmLedger(query),
        adapter.loadHistoricalDailyTbmLedger(query),
      ]);
      return summarizeDashboardTbm(current, historical, input.day);
    }),
    safe(can('work-permit'), async () => {
      const { getWorkLedgerPort } = await import('../work-control/work-ledger-port');
      const port = getWorkLedgerPort();
      let page = await port.list(scope);
      if (!page.connected) return null;
      const allRows = [...page.rows];
      const cursors = new Set<string>();
      let held = page.heldCount ?? 0;
      const summaryComplete = page.summary?.complete === true;
      while (!page.exhausted && page.nextCursor && cursors.size < 20) {
        const cursorKey = JSON.stringify(page.nextCursor);
        if (cursors.has(cursorKey)) break;
        cursors.add(cursorKey);
        page = await port.list(scope, page.nextCursor);
        if (!page.connected) throw Error('WORK_DASHBOARD_UNAVAILABLE');
        held += page.heldCount ?? 0;
        allRows.push(...page.rows);
      }
      const rows = [...new Map(allRows.map(row => [row.workId, row])).values()].filter(
        (row) =>
          row.lifecycle !== 'invalidated' &&
          seoulWorkDate(row.startsAt) <= input.day &&
          seoulWorkDate(row.endsAt) >= input.day,
      );
      return {
        rows,
        complete: page.exhausted === true && summaryComplete && !held,
      };
    }),
    safe(can('incident') && !!contractorId, async () => {
      const { createVictimRegistryPort } =
        await import('../incidents-emergency/victim-registry-data-adapter');
      const page = await createVictimRegistryPort(undefined, scope.siteId).list({
        contractorId,
        pageSize: 1,
        cursor: null,
        includeSummary: true,
        orderBy: 'occurredAt',
      });
      return authoritativeVictimTotal(page);
    }),
    safe(can('regular-risk') && !!contractorId, async () => {
      const { getRiskAuthoritativeAdapter } = await import('../risk-assessment/risk-data-adapter');
      const adapter = getRiskAuthoritativeAdapter();
      const listLedgerRecords = adapter.listLedgerRecords?.bind(adapter);
      if (!listLedgerRecords) throw Error('RISK_LEDGER_UNAVAILABLE');
      let page = await listLedgerRecords(scope, 'regular', 50);
      const records = [...page.items];
      const cursors = new Set<string>();
      while (page.nextCursor && cursors.size < 20 && !cursors.has(JSON.stringify(page.nextCursor))) {
        cursors.add(JSON.stringify(page.nextCursor));
        page = await listLedgerRecords(scope, 'regular', 50, page.nextCursor);
        records.push(...page.items);
      }
      const start =
        input.month.slice(0, 4) + (Number(input.month.slice(5)) <= 6 ? '-01-01' : '-07-01');
      const end =
        input.month.slice(0, 4) + (Number(input.month.slice(5)) <= 6 ? '-06-30' : '-12-31');
      const period = (row: typeof records[number]) => {
        if ('recordKind' in row) return row.sourceAssessmentPeriod;
        let { startsOn, endsOn } = row.assessmentPeriod;
        if (!startsOn || !endsOn) {
          const match = row.migrationContext?.sourceRound.match(/^(\d{4})[-년\s]*(상반기|하반기)$/u);
          if (!match) return null;
          startsOn = `${match[1]}-${match[2] === '상반기' ? '01-01' : '07-01'}`;
          endsOn = `${match[1]}-${match[2] === '상반기' ? '06-30' : '12-31'}`;
        }
        return { startsOn, endsOn };
      };
      const rows = records.filter(row => {
        const p = period(row);
        return p && p.startsOn <= end && p.endsOn >= start;
      });
      const year = input.month.slice(0, 4);
      const annual = records.filter(row => {
        const p = period(row);
        return p && p.startsOn <= `${year}-12-31` && p.endsOn >= `${year}-01-01`;
      });
      return {
        total: rows.length,
        draft: rows.filter((row) => !('recordKind' in row) && ['draft', 'revision-requested'].includes(row.stage)).length,
        annualTotal: annual.length,
        annualDraft: annual.filter(row => !('recordKind' in row) && ['draft', 'revision-requested'].includes(row.stage)).length,
        ...riskAnnualItemMetrics(records, year, undefined, page.nextCursor === null),
        complete: page.nextCursor === null,
      };
    }),
    safe(
      !contractorId &&
        [
          'regular-risk',
          'safety-training',
          'evacuation-drill',
          'inspection-reporting',
          'incident',
        ].some(can),
      async () => {
        const summary = await loadOperationsSummary({ ...input, siteId: scope.siteId, aggregation: 'daily',
        // Work rows are read above; the factory dashboard no longer renders TBM totals.
        features: input.features.filter(id => !['tbm', 'work-permit'].includes(id)),
        });
        onSummary?.(summary);
        return summary;
      },
    ),
  ]);
  return {
    tbm: contractorId ? tbm : (operations?.tbm ?? null),
    work,
    inspection: null,
    victimTotal,
    risk: contractorId ? risk : (operations?.risk ?? null),
    operations,
    errors,
    at: Date.now(),
  };
}
export type DashboardRead = Awaited<ReturnType<typeof readDashboard>>;
const cache = createSessionKeyedReadCache<DashboardRead>(8);
if (typeof window !== 'undefined') window.addEventListener(TBM_DATA_CHANGED_EVENT, () => cache.invalidate());
export function loadDashboard(input: Parameters<typeof readDashboard>[0], force = false, onSummary?: (summary: OperationsSummary) => void) {
  const key = JSON.stringify(input);
  const previous = cache.peek(key);
  return cache.read(
    key,
    () => readDashboard({ ...input, fresh: force }, onSummary),
    force || !previous || Date.now() - previous.at > 60_000,
  ).then(result => {
    if (result.errors.length || result.operations?.refreshPending) cache.invalidate(key);
    return result;
  });
}
